import {AfterContentInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, Observable, Subscription}                                 from 'rxjs';
import {ApElementOxydTypeEnum, ApGridPagerType, IGridPagerData}                  from '../../ap-interface';
import {ApPermissions}                                                           from 'invoker-transport';
import {
  ApFertilizerInorgsEditComponent
}                                                                                from '../../entry-components/ap-basedata-entry-components/ap-fertilizer-inorgs-edit.component';
import {
  ApDynComponentComponent
}                                                                                from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {GetElementOxidCalculatePipe, GetRoundNumericPipe}                        from '../../ap-utils';
import {
  ApDynGridStatusColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig,
  IDynGridWizardChoiceData
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  SettingsStore
}                                                                                from '../../stores/base-data/settings.store';
import {
  FertilizerInorganicStore
}                                                                                from '../../stores/base-data/fertilizer.inorganic.store';
import {filter, map}                                                             from 'rxjs/operators';
import {FormStore}                                                               from '../../stores/layout/form.store';
import {
  MapViewStore
}                                                                                from '../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                                from '../../stores/translation/translation.store';
import {
  GetPermissionPipe
}                                                                                from '../../ap-permission/pipes/get-permission.pipe';
import {ObjectFactory, SafeBehaviorSubject}                                      from 'ts-tooling';
import {APP_CONFIGURATION}                                                       from '../../ap-core/config';
import {
  GetElementService
}                                                                                from '../../ap-utils/service/ap-get-element.service';
import {
  ApTranslationService
}                                                                                from '../../ap-utils/service/ap-translation.service';
import {
  ApUtilService
}                                                                                from '../../ap-utils/service/ap-util.service';
import {
  DropDownButtonData
}                                                                                from '../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {
  ApElementType
}                                                                                from '../../ap-interface/enums/ap-elements-type.enum';
import {
  GetRoundNumericPipeConfig
}                                                                                from '../../ap-utils/pipes/configs/get-round-numeric.pipe.config';
import {
  FertilizationGridService
}                                                                                from '../services/fertilization-grid.service';
import {
  LanguageStore
}                                                                                from '../../stores/translation/language.store';
import {
  IFertilizerInorganicGridData,
  InorganicFertilizerActions
}                                                                                from '../types/fertilizer-inorgs.types';
import IFertilizerInorganic = Data.BaseData.IFertilizerInorg;
import ISettings = Data.BaseData.ISettings;

/**
 * Fertilizer inorganic overview component.
 * Shows a list of all Fertilizer inorganic.
 */
@Component({
  selector: 'ap-fertilizer-inorganic-overview',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="this.items$ | async"
                     [loading$]="fertilizerInorganicLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [canSearch]="true"
    ></ap-dyncomponent>
  `
})
export class ApFertilizerInorgsComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;
  public fertilizerInorganicLoading$: SafeBehaviorSubject<boolean> = new SafeBehaviorSubject<boolean>(false);
  public items$: Observable<any>;

  private _subscriptions: Array<Subscription> = [];
  private _applyClicked = new EventEmitter<any>();
  private _settings: ISettings;

  constructor(private store: FertilizerInorganicStore,
              private formStore: FormStore,
              private translationStore: TranslationStore,
              private mapViewStore: MapViewStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private permissionPipe: GetPermissionPipe,
              private roundNumericPipe: GetRoundNumericPipe,
              private elementOxideCalculate: GetElementOxidCalculatePipe,
              private elementService: GetElementService,
              private translationService: ApTranslationService,
              private fertilizationGridService: FertilizationGridService) {
  }

  public ngOnInit(): void {
    this.fertilizerInorganicLoading$ = this.store.Listen(s => s.loading);
    this.mapViewStore.hideMapView();
    this.items$ = this.store.Listen(s => s.data).pipe(
      filter(d => !!d),
      map(pp => pp.Convert<IFertilizerInorganicGridData>((inorganic: IFertilizerInorganic) => {
        const kValue = this._elementOxidCalculate(inorganic.ContentK, ApElementOxydTypeEnum.K);
        const mgValue = this._elementOxidCalculate(inorganic.ContentMg, ApElementOxydTypeEnum.Mg);
        const pValue = this._elementOxidCalculate(inorganic.ContentP, ApElementOxydTypeEnum.P);
        const isLiquid = inorganic.IsLiquid
          ? this.translationStore.FindTranslationForSelectedLanguage('Global__Aggregate_State_Liquid')
          : this.translationStore.FindTranslationForSelectedLanguage('Global__Aggregate_State_Solid');
        return {
          Id: inorganic.Id,
          SourceItem: inorganic,
          Status: inorganic.Selectable ? 1 : -1,
          StatusClass: inorganic.Selectable ? 'ap-status-green' : 'ap-status-red',
          StatusDesc: inorganic.Selectable ? 'Global__Active' : 'Global__Inactive',
          RealContentCaO: inorganic.RealContentCaO,
          CaOValue: inorganic.ContentCaO,
          KValue: kValue,
          MGValue: mgValue,
          NValue: inorganic.ContentN,
          PValue: pValue,
          SValue: inorganic.ContentS,
          Efficiency: inorganic.Efficiency,
          EquivCaO: inorganic.EquivCaO,
          Liquid: isLiquid,
          Density: inorganic.Density,
          Unit: this.translationStore.FindTranslationForSelectedLanguage(inorganic.Unit?.Key)
        };
      }))
    );
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
    this._getDataFromStore();
    this._gridBuilder();
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(d => d.unsubscribe());
  }

  public onEdit(dataItem: any): void {
    this.formStore.openForm({component: ApFertilizerInorgsEditComponent, data: dataItem.SourceItem});
  }

  private _getDataFromStore(): void {
    this._subscriptions.push(this.settingsStore.FirstSetting$.subscribe(s => this._settings = s));
  }

  private _gridBuilder(): void {
    this._subscriptions.push(
      combineLatest([
        this.permissionPipe.transform(ApPermissions.EDIT_FERTILIZER),
        this.languageStore.SelectedLanguage$
      ]).pipe(
        filter(([permission, language]) => !!permission && !!language)
      ).subscribe(([permission, language]) => {
        this.columns = [
          new ApDynGridStatusColumnConfig({
            field: 'Status',
            class: 'StatusClass',
            description: 'StatusDesc',
            showHeaderTitle: true,
            filterable: true,
            groupIndex: 0,
            groupDesc: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.Prodname',
            title: 'Global__Name',
            filterable: true,
            width: 100,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridGroupColumnConfig({
            title: this.getLabelWithUnit('Global__Content', '%'),
            headerStyle: {'text-align': 'center'},
            style: {border: '1px solid #2c3237'},
            groupColumns: [
              new ApDynGridPropertyColumnConfig({
                field: 'PValue',
                title: this.elementService.GetElementStringByString(ApElementType.P),
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
              new ApDynGridPropertyColumnConfig({
                field: 'KValue',
                title: this.elementService.GetElementStringByString(ApElementType.K),
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
              new ApDynGridPropertyColumnConfig({
                field: 'MGValue',
                title: this.elementService.GetElementStringByString(ApElementType.Mg),
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
              new ApDynGridPropertyColumnConfig({
                field: 'CaOValue',
                title: 'Global__NutrientCaO',
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
              new ApDynGridPropertyColumnConfig({
                field: 'NValue',
                title: 'Global__NutrientN',
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
              new ApDynGridPropertyColumnConfig({
                field: 'SValue',
                title: 'Global__NutrientS',
                formatCallback: this.fertilizationGridService.formatNumericValue(
                  APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent, language),
                cssClass: 'right',
                filterType: FilterType.NUMBER,
                width: 60,
                pipes: [{
                  pipe: this.roundNumericPipe,
                  args: [
                    APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertContent,
                    GetRoundNumericPipeConfig.NullAndZeroToEmptyReplaceConfig
                  ]
                }]
              }),
            ]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Unit',
            title: 'Settings__Lbl_Menu_Unit',
            width: 40,
            filterable: true,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Efficiency',
            title: 'Settings__Lbl_NEffectiveness',
            cssClass: 'right',
            width: 60,
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [this._settings.DigitsAfterDecimalPoint]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'EquivCaO',
            title: 'Nutrients_Pages__PartOfCaO',
            formatCallback: this.fertilizationGridService.formatNumericValue(
              APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertCaO, language),
            cssClass: 'right',
            width: 60,
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertCaO]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Liquid',
            title: 'Global__Aggregate',
            width: 60,
            filterable: true,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'Density',
            title: 'Global__Density',
            formatCallback: this.fertilizationGridService.formatNumericValue(
              APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertDensity, language),
            cssClass: 'right',
            width: 60,
            filterType: FilterType.NUMBER,
            filterable: true,
            headerFilterable: true,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.InorgFertDensity]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SourceItem.Note',
            title: 'Global__Note',
            width: 180,
            filterable: true,
            headerFilterable: true,
            hide: true
          }),
          new ApDynGridEditColumnConfig({
            data: this._getEditButtons(permission)
          })
        ];
        this.pager = new ApDynGridPagerConfig({
          listMassEditing: [
            {
              id: InorganicFertilizerActions.Active,
              name: this.translationStore.FindTranslationForSelectedLanguage('Global__Enable'),
              type: ApGridPagerType.None,
              values: null
            } as IDynGridWizardChoiceData,
            {
              id: InorganicFertilizerActions.Inactive,
              name: this.translationStore.FindTranslationForSelectedLanguage('Global__Disable'),
              type: ApGridPagerType.None,
              values: null
            } as IDynGridWizardChoiceData,
          ],
          apply: this._applyClicked,
          hasPermission: permission,
          preselect: true
        });
      })
    );
  }

  private _onApplyClicked(data: IGridPagerData): void {
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach(row => {
      const item = ObjectFactory.Copy(row['SourceItem'] as IFertilizerInorganic);
      if (item) {
        item.Selectable = data.object.id === InorganicFertilizerActions.Active;
        this.store.updateSelectableFertilizer(item);
      }
    });
  }

  private _elementOxidCalculate(value: number, unit: ApElementOxydTypeEnum): number {
    return this.elementOxideCalculate.transform(
      value, unit, this._settings.UnitElementOxyd
    );
  }

  private getLabelWithUnit(text: string, unit: string): string {
    return this.translationService.translate(text) + ' [' + unit + ']';
  }

  private _getEditButtons(permission: boolean): DropDownButtonData[] {
    const result: DropDownButtonData[] = [];
    if (permission) {
      result.push({
        text: 'Global__Edit',
        disabled: (dataItem) => !(dataItem?.SourceItem.FarmId > 0),
        callback: ApUtilService.createEventEmitter(this.onEdit.bind(this), this._subscriptions)
      });
    }
    result.push({
      callback: ApUtilService.createEventEmitter(this._onSelectedToggle.bind(this), this._subscriptions),
      text: (dataItem) => dataItem.SourceItem.Selectable ? 'Global__Disable' : 'Global__Enable'
    });

    return result;
  }

  private _onSelectedToggle(dataItem: any): void {
    const fertilizerInorganic = ObjectFactory.Copy(
      this.store.Listen(s => s.data).getValue().Find((d) => !!dataItem && d?.Id === dataItem?.Id)
    );
    fertilizerInorganic.Selectable = !fertilizerInorganic.Selectable;
    this.store.updateSelectableFertilizer(fertilizerInorganic);
  }
}
